import type { ServiceTicket, ServiceTicketActivity, ServiceTicketPriority, ServiceTicketStatus, ServiceTicketCategory } from "@/data/serviceTicketTypes";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export type ResolveServiceTab = "active" | "archived";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface ResolveServiceTicketsPageQuery {
  tab: ResolveServiceTab;
  page: number;
  pageSize: number;
  search?: string;
  status?: ServiceTicketStatus | "all";
  refreshToken?: number;
}

export interface ResolveServiceSummary {
  total: number;
  active: number;
  archived: number;
  open: number;
  inProgress: number;
  awaitingParts: number;
  scheduled: number;
  resolved: number;
  closed: number;
  urgentActive: number;
}

export interface ResolveServiceJobOption {
  jobId: string;
  jobLabel: string;
  customerName: string;
  currentStage: string;
  sortAt: string;
}

type ServiceTicketViewRow = {
  row_id?: string | null;
  ticket_id?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  customer_id?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  address?: string | null;
  category?: ServiceTicketCategory | string | null;
  priority?: ServiceTicketPriority | string | null;
  status?: ServiceTicketStatus | string | null;
  subject?: string | null;
  description?: string | null;
  resolution?: string | null;
  resolved_at?: string | null;
  resolved_by?: string | null;
  assigned_to?: string | null;
  created_by?: string | null;
  scheduled_date?: string | null;
  scheduled_time_slot?: string | null;
  activities?: ServiceTicketActivity[] | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  ticket_bucket?: ResolveServiceTab | null;
  sort_at?: string | null;
};

type ResolveServiceSummaryViewRow = {
  total?: number | string | null;
  active?: number | string | null;
  archived?: number | string | null;
  open?: number | string | null;
  in_progress?: number | string | null;
  awaiting_parts?: number | string | null;
  scheduled?: number | string | null;
  resolved?: number | string | null;
  closed?: number | string | null;
  urgent_active?: number | string | null;
};

type ResolveServiceJobOptionViewRow = {
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  current_stage?: string | null;
  sort_at?: string | null;
};

const TICKET_COLUMNS = [
  "row_id",
  "ticket_id",
  "job_id",
  "job_label",
  "customer_id",
  "customer_name",
  "customer_phone",
  "customer_email",
  "address",
  "category",
  "priority",
  "status",
  "subject",
  "description",
  "resolution",
  "resolved_at",
  "resolved_by",
  "assigned_to",
  "created_by",
  "scheduled_date",
  "scheduled_time_slot",
  "activities",
  "notes",
  "created_at",
  "updated_at",
  "ticket_bucket",
  "sort_at",
].join(",");

const SUMMARY_COLUMNS = [
  "total",
  "active",
  "archived",
  "open",
  "in_progress",
  "awaiting_parts",
  "scheduled",
  "resolved",
  "closed",
  "urgent_active",
].join(",");

const JOB_OPTION_COLUMNS = "job_id,job_label,customer_name,current_stage,sort_at";

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function dateOnly(value: string | null | undefined): string {
  if (!value) return "";
  return String(value).split("T")[0] ?? "";
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  const startedAt = observeQueryStart(key);
  try {
    const result = await run();
    recordQueryMetric({ key, startedAt, status: "success" });
    return result;
  } catch (error) {
    recordQueryMetric({ key, startedAt, status: "error", error });
    throw error;
  }
}

function mapTicket(row: ServiceTicketViewRow): ServiceTicket {
  const id = String(row.ticket_id ?? row.row_id ?? "");
  return {
    id,
    jobId: String(row.job_label ?? row.job_id ?? ""),
    customerId: String(row.customer_id ?? ""),
    customerName: String(row.customer_name ?? "Unknown"),
    customerPhone: String(row.customer_phone ?? ""),
    customerEmail: String(row.customer_email ?? ""),
    address: String(row.address ?? ""),
    category: (row.category ?? "Other") as ServiceTicketCategory,
    priority: (row.priority ?? "Medium") as ServiceTicketPriority,
    status: (row.status ?? "Open") as ServiceTicketStatus,
    subject: String(row.subject ?? "Service ticket"),
    description: String(row.description ?? ""),
    resolution: row.resolution ?? undefined,
    resolvedAt: row.resolved_at ? dateOnly(row.resolved_at) : undefined,
    resolvedBy: row.resolved_by ?? undefined,
    assignedTo: String(row.assigned_to ?? ""),
    createdBy: String(row.created_by ?? ""),
    createdAt: dateOnly(row.created_at),
    updatedAt: dateOnly(row.updated_at),
    activities: Array.isArray(row.activities) ? row.activities : [],
    scheduledDate: row.scheduled_date ?? undefined,
    scheduledTimeSlot: row.scheduled_time_slot ?? undefined,
    notes: String(row.notes ?? ""),
  };
}

function mapSummary(row: ResolveServiceSummaryViewRow | null | undefined): ResolveServiceSummary {
  return {
    total: num(row?.total),
    active: num(row?.active),
    archived: num(row?.archived),
    open: num(row?.open),
    inProgress: num(row?.in_progress),
    awaitingParts: num(row?.awaiting_parts),
    scheduled: num(row?.scheduled),
    resolved: num(row?.resolved),
    closed: num(row?.closed),
    urgentActive: num(row?.urgent_active),
  };
}

function mapJobOption(row: ResolveServiceJobOptionViewRow): ResolveServiceJobOption {
  return {
    jobId: String(row.job_id ?? ""),
    jobLabel: String(row.job_label ?? row.job_id ?? ""),
    customerName: String(row.customer_name ?? "Unknown"),
    currentStage: String(row.current_stage ?? ""),
    sortAt: String(row.sort_at ?? ""),
  };
}

export const ResolveServicePageService = {
  async getTicketsPage(query: ResolveServiceTicketsPageQuery): Promise<PageResponse<ServiceTicket>> {
    const key = `resolve-service:tickets:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      const from = Math.max(0, query.page) * Math.max(1, query.pageSize);
      const to = from + Math.max(1, query.pageSize) - 1;
      let builder = supabase
        .from("v_resolve_service_tickets_ui")
        .select(TICKET_COLUMNS, { count: "exact" })
        .eq("ticket_bucket", query.tab);

      if (query.status && query.status !== "all") {
        builder = builder.eq("status", query.status);
      }

      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `ticket_id.ilike.${pattern}`,
          `subject.ilike.${pattern}`,
          `customer_name.ilike.${pattern}`,
          `job_label.ilike.${pattern}`,
        ].join(","));
      }

      builder = builder
        .order("sort_at", { ascending: false })
        .range(from, to);

      const { data, error, count } = await builder;
      return {
        data: error || !data ? [] : (data as ServiceTicketViewRow[]).map(mapTicket),
        count: count ?? 0,
        error,
      };
    });
  },

  async getSummary(refreshToken?: number): Promise<{ data: ResolveServiceSummary; error: unknown | null }> {
    const key = `resolve-service:summary:${refreshToken ?? 0}`;
    return withPageMetrics(key, async () => {
      const { data, error } = await supabase
        .from("v_resolve_service_summary")
        .select(SUMMARY_COLUMNS)
        .maybeSingle();

      return { data: mapSummary(data as ResolveServiceSummaryViewRow | null), error };
    });
  },

  async getJobOptions(query: { search?: string; limit?: number; refreshToken?: number } = {}): Promise<PageResponse<ResolveServiceJobOption>> {
    const key = `resolve-service:job-options:${hashFilters(query)}`;
    return withPageMetrics(key, async () => {
      let builder = supabase
        .from("v_resolve_service_job_options_ui")
        .select(JOB_OPTION_COLUMNS, { count: "exact" });

      const search = query.search?.trim();
      if (search) {
        const pattern = sanitizedSearchPattern(search);
        builder = builder.or([
          `job_label.ilike.${pattern}`,
          `customer_name.ilike.${pattern}`,
        ].join(","));
      }

      builder = builder
        .order("sort_at", { ascending: false })
        .limit(query.limit ?? 50);

      const { data, error, count } = await builder;
      return {
        data: error || !data ? [] : (data as ResolveServiceJobOptionViewRow[]).map(mapJobOption),
        count: count ?? 0,
        error,
      };
    });
  },
};
